import React from 'react';
import { 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  Info, 
  X 
} from 'lucide-react';

const NotificationItem = ({ notification, onClose }) => {
  const { id, type, message, title } = notification;

  const styles = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      progressBar: 'bg-green-500'
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: <XCircle className="w-5 h-5 text-red-500" />,
      progressBar: 'bg-red-500'
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
      progressBar: 'bg-amber-500'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      icon: <Info className="w-5 h-5 text-blue-500" />,
      progressBar: 'bg-blue-500'
    }
  }[type || 'info'];

  return (
    <div 
      className={`
        flex min-w-[320px] max-w-md items-start items-center gap-4 p-4 
        rounded-xl shadow-xl border animate-in slide-in-from-right-full 
        ${styles.bg} ${styles.border} ${styles.text} 
        relative overflow-hidden group transition-all duration-300
      `}
    >
      <div className="flex-shrink-0">
        {styles.icon}
      </div>
      
      <div className="flex-grow">
        {title && <h3 className="font-bold text-sm mb-0.5">{title}</h3>}
        <p className="text-sm font-medium leading-tight">
          {message}
        </p>
      </div>

      <button 
        onClick={() => onClose(id)}
        className="flex-shrink-0 hover:bg-black/5 p-1 rounded-full transition-colors"
      >
        <X className="w-4 h-4 opacity-50 group-hover:opacity-100" />
      </button>

      {/* Progress bar for auto-dismiss - simplified animation */}
      {notification.duration !== Infinity && (
        <div className="absolute bottom-0 left-0 h-1 w-full bg-black/5">
          <div 
            className={`h-full ${styles.progressBar} opacity-30`}
            style={{ 
              animation: `progress-shrink ${notification.duration || 3000}ms linear forwards` 
            }}
          />
        </div>
      )}
    </div>
  );
};

export const CreateNotificationOverlay = ({ notifications, onClose }) => {
  if (!notifications.length) return null;

  return (
    <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
      <div className="pointer-events-auto flex flex-col gap-3">
        {notifications.map((notification) => (
          <NotificationItem 
            key={notification.id} 
            notification={notification} 
            onClose={onClose} 
          />
        ))}
      </div>
      
      <style>{`
        @keyframes progress-shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        .animate-in {
          animation-duration: 400ms;
          animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .slide-in-from-right-full {
          animation-name: slide-in-from-right;
        }

        @keyframes slide-in-from-right {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export const ConfirmModal = ({ 
  title = 'Conferma', 
  message, 
  confirmText = 'Conferma', 
  cancelText = 'Annulla', 
  onConfirm, 
  onClose,
  type = 'warning'
}) => {
  const styles = {
    warning: {
      icon: <AlertCircle className="w-8 h-8 text-amber-500" />,
      btn: 'bg-amber-600 hover:bg-amber-700',
      bg: 'bg-amber-50'
    },
    danger: {
      icon: <XCircle className="w-8 h-8 text-red-500" />,
      btn: 'bg-red-600 hover:bg-red-700',
      bg: 'bg-red-50'
    },
    info: {
      icon: <Info className="w-8 h-8 text-blue-500" />,
      btn: 'bg-blue-600 hover:bg-blue-700',
      bg: 'bg-blue-50'
    }
  }[type] || styles.warning;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="p-6 flex flex-col items-center text-center">
          <div className={`p-3 rounded-full ${styles.bg} mb-4`}>
            {styles.icon}
          </div>
          
          <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 mb-8 px-2">
            {message}
          </p>
          
          <div className="flex gap-3 w-full">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-4 py-2.5 text-white rounded-xl font-semibold shadow-lg transition-all active:scale-95 ${styles.btn}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
